'use client';

import { useState, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  BadgeMemoryStatus,
  EndpointMemoryEditButton,
  Text,
  Table,
  IconButton,
  Tooltip,
  LegacyDialog,
  Button,
  Field,
  Textarea,
  Spinner,
  Tabs,
  Scroll,
} from '@terra/ui-design-system';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useAppTargetId } from '../../store/projectStore';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { BrainIcon, Edit02Icon, ViewIcon, Delete02Icon } from '@terra/ui-icons';
import { EmptyState } from '../EmptyState';
import { MemoriesFilterBar } from './MemoriesFilterBar';
import { MemoriesToolbarActions } from './MemoriesToolbarActions';
import { useMemoriesFilterStore } from '../../store/memoriesFilterStore';
import { useMemories } from '../../hooks/useMemories';
import { useMemoryActions } from '../../hooks/useMemoryActions';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import type { MemoryRecordResponse } from '@terra/shared-contracts-memory';
import { formatMemoryRecordIdForDisplay } from '@terra/shared-contracts-memory';

export function MemoriesContent() {
  const appTargetId = useAppTargetId();
  const dateFormatter = useDateFormatter();
  const t = useTranslations('memories');
  const searchTerm = useMemoriesFilterStore((state) => state.searchTerm);
  const setSearchTerm = useMemoriesFilterStore((state) => state.setSearchTerm);
  const [activeTab, setActiveTab] = useState<
    'application' | 'user_preferences'
  >('application');
  const {
    memories,
    totalCount,
    isLoading: isLoadingMemories,
    refetch: refetchMemories,
  } = useMemories({ applicationId: appTargetId, searchTerm });
  const {
    preferences,
    totalCount: preferencesTotalCount,
    isLoading: isLoadingPreferences,
    createPreference,
    deletePreference,
    updatePreference,
    isActionInProgress: isPreferenceActionInProgress,
  } = useUserPreferences({ applicationId: appTargetId });
  const {
    actionState,
    createMemory,
    updateMemory,
    deleteMemory,
    isActionInProgress,
  } = useMemoryActions({
    applicationId: appTargetId,
    onSuccess: refetchMemories,
  });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedMemory, setSelectedMemory] =
    useState<MemoryRecordResponse | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewMemory, setViewMemory] = useState<MemoryRecordResponse | null>(
    null,
  );
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newMemoryContent, setNewMemoryContent] = useState('');
  const [isCreatePreferenceDialogOpen, setIsCreatePreferenceDialogOpen] =
    useState(false);
  const [newPreferenceContent, setNewPreferenceContent] = useState('');
  const handleView = useCallback((memory: MemoryRecordResponse) => {
    setViewMemory(memory);
    setIsViewDialogOpen(true);
  }, []);
  const handleEdit = useCallback((memory: MemoryRecordResponse) => {
    setSelectedMemory(memory);
    setEditedContent(memory.content);
    setIsEditDialogOpen(true);
  }, []);
  const handleSaveEdit = useCallback(async () => {
    if (!selectedMemory) return;
    const updateFn =
      selectedMemory.strategy === 'user_preferences'
        ? updatePreference
        : updateMemory;
    const success = await updateFn(selectedMemory.recordId, editedContent);

    if (success) {
      setIsEditDialogOpen(false);
      setSelectedMemory(null);
      setEditedContent('');
    }
  }, [selectedMemory, editedContent, updateMemory, updatePreference]);
  const handleCreateMemory = useCallback(async () => {
    const success = await createMemory(newMemoryContent);

    if (success) {
      setIsCreateDialogOpen(false);
      setNewMemoryContent('');
    }
  }, [newMemoryContent, createMemory]);
  const handleCreatePreference = useCallback(async () => {
    const success = await createPreference(newPreferenceContent);

    if (success) {
      setIsCreatePreferenceDialogOpen(false);
      setNewPreferenceContent('');
    }
  }, [newPreferenceContent, createPreference]);
  const handleAddClick = useCallback(() => {
    if (activeTab === 'user_preferences') {
      setIsCreatePreferenceDialogOpen(true);

      return;
    }

    setIsCreateDialogOpen(true);
  }, [activeTab]);
  const toolbarActions = useMemo(
    () => (
      <MemoriesToolbarActions
        activeTab={activeTab}
        onAddClick={handleAddClick}
      />
    ),
    [activeTab, handleAddClick],
  );

  const ActionButtonsRenderer = useCallback(
    ({ data: memory }: { data: MemoryRecordResponse | undefined }) => {
      if (!memory) return null;
      const loading = isActionInProgress(memory.recordId);
      const status = memory.status ?? 'active';
      const statusAllowsEdit = status === 'active' || status === 'rejected';

      return (
        <HStack gap="1" justifyContent="center" alignItems="center" h="full">
          <Tooltip title="View details">
            <IconButton
              aria-label="View"
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleView(memory);
              }}
            >
              <ViewIcon size={14} />
            </IconButton>
          </Tooltip>

          {statusAllowsEdit ? (
            <EndpointMemoryEditButton
              creationMethod={memory.creationMethod}
              loading={loading}
              onEdit={() => {
                handleEdit(memory);
              }}
            />
          ) : null}

          <Tooltip title="Delete">
            <IconButton
              aria-label="Delete"
              size="xs"
              variant="ghost"
              disabled={loading}
              onClick={(e) => {
                e.stopPropagation();
                deleteMemory(memory);
              }}
            >
              {loading ? <Spinner size="xs" /> : <Delete02Icon size={14} />}
            </IconButton>
          </Tooltip>
        </HStack>
      );
    },
    [handleView, handleEdit, deleteMemory, isActionInProgress],
  );
  const PreferenceActionButtonsRenderer = useCallback(
    ({ data: memory }: { data: MemoryRecordResponse | undefined }) => {
      if (!memory) return null;
      const loading = isPreferenceActionInProgress(memory.recordId);

      return (
        <HStack gap="1" justifyContent="center" alignItems="center" h="full">
          <Tooltip title={t('preferences.viewTooltip')}>
            <IconButton
              aria-label={t('preferences.viewAriaLabel')}
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleView(memory);
              }}
            >
              <ViewIcon size={14} />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('preferences.editTooltip')}>
            <IconButton
              aria-label={t('preferences.editAriaLabel')}
              size="xs"
              variant="ghost"
              disabled={loading}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(memory);
              }}
            >
              <Edit02Icon size={14} />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('preferences.deleteTooltip')}>
            <IconButton
              aria-label={t('preferences.deleteAriaLabel')}
              size="xs"
              variant="ghost"
              disabled={loading}
              onClick={(e) => {
                e.stopPropagation();
                deletePreference(memory.recordId);
              }}
            >
              {loading ? <Spinner size="xs" /> : <Delete02Icon size={14} />}
            </IconButton>
          </Tooltip>
        </HStack>
      );
    },
    [handleView, handleEdit, deletePreference, isPreferenceActionInProgress, t],
  );
  const columnDefs = useMemo<ColDef<MemoryRecordResponse>[]>(
    () => [
      {
        headerName: 'Content',
        field: 'content',
        flex: 1,
        minWidth: 300,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => {
          if (!params.data) return null;

          return (
            <Text
              fontSize="xs"
              color="fg.default"
              truncate
              title={params.data.content}
            >
              {params.data.content}
            </Text>
          );
        },
      },
      {
        headerName: t('columnCreationMethod'),
        field: 'creationMethod',
        width: 140,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => {
          const method = params.data?.creationMethod ?? 'manual';

          return (
            <Tooltip title={t(`creationMethodTooltip.${method}`)}>
              <Text fontSize="xs" color="fg.default">
                {t(`creationMethod.${method}`)}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        headerName: t('columnStatus'),
        field: 'status',
        width: 150,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => {
          const status = params.data?.status ?? 'active';
          const tooltip =
            status === 'rejected' && params.data?.rejectionReason
              ? params.data.rejectionReason
              : t(`statusTooltip.${status}`);

          return (
            <Tooltip title={tooltip}>
              <BadgeMemoryStatus status={status}>
                {t(`status.${status}`)}
              </BadgeMemoryStatus>
            </Tooltip>
          );
        },
      },
      {
        headerName: 'Created',
        field: 'createdAt',
        width: 160,
        sortable: true,
        sort: 'desc',
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => {
          if (!params.data?.createdAt) {
            return (
              <Text fontSize="xs" color="fg.muted">
                -
              </Text>
            );
          }

          return (
            <Text fontSize="xs" color="fg.muted">
              {dateFormatter.format(new Date(params.data.createdAt))}
            </Text>
          );
        },
      },
      {
        headerName: 'Actions',
        width: 150,
        suppressNavigable: true,
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => (
          <ActionButtonsRenderer data={params.data} />
        ),
        filter: false,
        sortable: false,
        resizable: false,
      },
    ],
    [dateFormatter, ActionButtonsRenderer, t],
  );
  const hasActiveFilters = searchTerm !== '';
  const preferencesColumnDefs = useMemo<ColDef<MemoryRecordResponse>[]>(
    () => [
      {
        headerName: t('preferences.columnPreference'),
        field: 'content',
        flex: 1,
        minWidth: 300,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => {
          if (!params.data) return null;

          return (
            <Text
              fontSize="xs"
              color="fg.default"
              truncate
              title={params.data.content}
            >
              {params.data.content}
            </Text>
          );
        },
      },
      {
        headerName: t('preferences.columnSaved'),
        field: 'createdAt',
        width: 160,
        sortable: true,
        sort: 'desc',
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => {
          if (!params.data?.createdAt) {
            return (
              <Text fontSize="xs" color="fg.muted">
                -
              </Text>
            );
          }

          return (
            <Text fontSize="xs" color="fg.muted">
              {dateFormatter.format(new Date(params.data.createdAt))}
            </Text>
          );
        },
      },
      {
        headerName: t('preferences.columnActions'),
        width: 150,
        suppressNavigable: true,
        cellRenderer: (params: ICellRendererParams<MemoryRecordResponse>) => (
          <PreferenceActionButtonsRenderer data={params.data} />
        ),
        filter: false,
        sortable: false,
        resizable: false,
      },
    ],
    [dateFormatter, PreferenceActionButtonsRenderer, t],
  );

  if (isLoadingMemories && activeTab === 'application') {
    return (
      <EmptyState
        icon={BrainIcon}
        title="Loading memories..."
        isLoading={true}
      />
    );
  }

  return (
    <VStack h="full" gap="0">
      <Tabs.Root
        value={activeTab}
        onValueChange={(details) =>
          setActiveTab(details.value as 'application' | 'user_preferences')
        }
        size="sm"
        w="full"
        flex="1"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        minH="0"
      >
        <HStack
          justifyContent="space-between"
          alignItems="center"
          w="full"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          pe="3"
        >
          <Tabs.List justifyContent="flex-start">
            <Tabs.Trigger value="application" fontSize="sm" px="3" py="2">
              {t('tabs.applicationMemories')}
              {totalCount > 0 ? ` (${totalCount})` : ''}
            </Tabs.Trigger>
            <Tabs.Trigger value="user_preferences" fontSize="sm" px="3" py="2">
              {t('tabs.myPreferences')}
              {preferencesTotalCount > 0 ? ` (${preferencesTotalCount})` : ''}
            </Tabs.Trigger>
            <Tabs.Indicator />
          </Tabs.List>
          {toolbarActions}
        </HStack>

        <Tabs.Content value="application" flex="1" overflow="hidden" pt={0}>
          <VStack h="full" gap="0" minH="0">
            <MemoriesFilterBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filteredCount={totalCount}
            />
            <Scroll flex="1">
              <Box p="4" h="full" minH="0">
                {memories.length === 0 ? (
                  <EmptyState
                    icon={BrainIcon}
                    title="No Memories Found"
                    description={
                      hasActiveFilters
                        ? 'Try adjusting your search to see more results.'
                        : 'No memories stored for this application yet.'
                    }
                  />
                ) : (
                  <Box
                    flex="1"
                    w="full"
                    h="full"
                    overflow="hidden"
                    minH="400px"
                  >
                    <Table
                      key={`memories-table-${searchTerm}`}
                      columnDefs={columnDefs}
                      rowData={memories}
                      rowHeight={44}
                      headerHeight={36}
                      noBorder={true}
                      fontSize={12}
                      headerFontSize={11}
                      animateRows={false}
                      rowBuffer={100}
                      debounceVerticalScrollbar={true}
                      defaultColDef={{
                        sortable: true,
                        filter: false,
                        resizable: true,
                      }}
                      suppressCellFocus={true}
                      getRowId={(params) => params.data.recordId}
                      domLayout="normal"
                    />
                  </Box>
                )}
              </Box>
            </Scroll>
          </VStack>
        </Tabs.Content>

        <Tabs.Content
          value="user_preferences"
          flex="1"
          overflow="hidden"
          pt={0}
        >
          <Scroll flex="1">
            <Box p="4" h="full" minH="0">
              {isLoadingPreferences ? (
                <EmptyState
                  icon={BrainIcon}
                  title={t('preferences.loadingTitle')}
                  isLoading={true}
                />
              ) : preferences.length === 0 ? (
                <EmptyState
                  icon={BrainIcon}
                  title={t('preferences.emptyTitle')}
                  description={t('preferences.emptyDescription')}
                />
              ) : (
                <Box flex="1" w="full" h="full" overflow="hidden" minH="400px">
                  <Table
                    columnDefs={preferencesColumnDefs}
                    rowData={preferences}
                    rowHeight={44}
                    headerHeight={36}
                    noBorder={true}
                    fontSize={12}
                    headerFontSize={11}
                    animateRows={false}
                    rowBuffer={100}
                    debounceVerticalScrollbar={true}
                    defaultColDef={{
                      sortable: true,
                      filter: false,
                      resizable: true,
                    }}
                    suppressCellFocus={true}
                    getRowId={(params) => params.data.recordId}
                    domLayout="normal"
                  />
                </Box>
              )}
            </Box>
          </Scroll>
        </Tabs.Content>
      </Tabs.Root>

      {/* View Memory Dialog */}
      <LegacyDialog.Root
        open={isViewDialogOpen}
        onOpenChange={({ open }) => {
          setIsViewDialogOpen(open);
          if (!open) setViewMemory(null);
        }}
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="xl">
            <VStack gap="3" p="3">
              <VStack gap="1" alignItems="flex-start" w="full">
                <LegacyDialog.Title>Memory Details</LegacyDialog.Title>
              </VStack>

              {viewMemory && (
                <VStack gap="2" alignItems="flex-start" w="full">
                  <Field.Root w="full">
                    <Field.Label fontSize="xs">Content</Field.Label>
                    <Box
                      p="2"
                      bg="bg.subtle"
                      borderRadius="md"
                      w="full"
                      maxH="300px"
                      overflow="auto"
                    >
                      <Text fontSize="xs" whiteSpace="pre-wrap">
                        {viewMemory.content}
                      </Text>
                    </Box>
                  </Field.Root>
                  <HStack gap="4" w="full">
                    <Field.Root flex="1">
                      <Field.Label fontSize="xs">Record ID</Field.Label>
                      <Text fontSize="xs" color="fg.muted" fontFamily="mono">
                        {formatMemoryRecordIdForDisplay(viewMemory.recordId)}
                      </Text>
                    </Field.Root>
                    {viewMemory.createdAt && (
                      <Field.Root>
                        <Field.Label fontSize="xs">Created</Field.Label>
                        <Text fontSize="xs" color="fg.muted">
                          {dateFormatter.format(new Date(viewMemory.createdAt))}
                        </Text>
                      </Field.Root>
                    )}
                  </HStack>
                </VStack>
              )}

              <HStack gap="2" w="full" justifyContent="flex-end">
                <LegacyDialog.CloseTrigger asChild>
                  <Button size="sm" variant="outline">
                    Close
                  </Button>
                </LegacyDialog.CloseTrigger>
              </HStack>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>

      {/* Edit Memory Dialog */}
      <LegacyDialog.Root
        open={isEditDialogOpen}
        onOpenChange={({ open }) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setSelectedMemory(null);
            setEditedContent('');
          }
        }}
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="xl">
            <VStack gap="3" p="3">
              <VStack gap="1" alignItems="flex-start" w="full">
                <LegacyDialog.Title>Edit Memory</LegacyDialog.Title>
                <LegacyDialog.Description>
                  Edit the memory content. This will create a correction event
                  in the memory system.
                </LegacyDialog.Description>
              </VStack>

              <VStack gap="2" alignItems="flex-start" w="full">
                {selectedMemory && (
                  <Field.Root w="full">
                    <Field.Label fontSize="xs">Record ID</Field.Label>
                    <Text fontSize="xs" color="fg.muted" fontFamily="mono">
                      {formatMemoryRecordIdForDisplay(selectedMemory.recordId)}
                    </Text>
                  </Field.Root>
                )}
                <Field.Root w="full">
                  <Field.Label fontSize="xs">Content</Field.Label>
                  <Textarea
                    size="sm"
                    value={editedContent}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setEditedContent(e.target.value)
                    }
                    rows={6}
                    resize="vertical"
                  />
                </Field.Root>
              </VStack>

              <HStack gap="2" w="full" justifyContent="flex-end">
                <LegacyDialog.CloseTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={
                      selectedMemory?.strategy === 'user_preferences'
                        ? isPreferenceActionInProgress(selectedMemory.recordId)
                        : actionState.isSubmitting
                    }
                  >
                    Cancel
                  </Button>
                </LegacyDialog.CloseTrigger>
                <Button
                  size="sm"
                  colorPalette="terra"
                  onClick={handleSaveEdit}
                  loading={
                    selectedMemory?.strategy === 'user_preferences'
                      ? isPreferenceActionInProgress(selectedMemory.recordId)
                      : actionState.isSubmitting
                  }
                  disabled={!editedContent.trim()}
                >
                  Save Changes
                </Button>
              </HStack>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>

      {/* Create Memory Dialog */}
      <LegacyDialog.Root
        open={isCreateDialogOpen}
        onOpenChange={({ open }) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            setNewMemoryContent('');
          }
        }}
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="xl">
            <VStack gap="3" p="3">
              <VStack gap="1" alignItems="flex-start" w="full">
                <LegacyDialog.Title>Add Memory</LegacyDialog.Title>
                <LegacyDialog.Description>
                  Create a new memory event. This will be processed by Agent
                  Core and added to the memory store.
                </LegacyDialog.Description>
              </VStack>

              <VStack gap="2" alignItems="flex-start" w="full">
                <Field.Root w="full">
                  <Field.Label fontSize="xs">Memory Content</Field.Label>
                  <Textarea
                    size="sm"
                    value={newMemoryContent}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setNewMemoryContent(e.target.value)
                    }
                    placeholder="Enter the memory content..."
                    rows={6}
                    resize="vertical"
                  />
                  <Field.HelperText fontSize="xs">
                    The memory will be associated with this application and
                    processed by Memory Provider.
                  </Field.HelperText>
                </Field.Root>
              </VStack>

              <HStack gap="2" w="full" justifyContent="flex-end">
                <LegacyDialog.CloseTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={actionState.isSubmitting}
                  >
                    Cancel
                  </Button>
                </LegacyDialog.CloseTrigger>
                <Button
                  size="sm"
                  colorPalette="terra"
                  onClick={handleCreateMemory}
                  loading={actionState.isSubmitting}
                  disabled={!newMemoryContent.trim()}
                >
                  Create Memory
                </Button>
              </HStack>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>

      {/* Create User Preference Dialog */}
      <LegacyDialog.Root
        open={isCreatePreferenceDialogOpen}
        onOpenChange={({ open }) => {
          setIsCreatePreferenceDialogOpen(open);
          if (!open) {
            setNewPreferenceContent('');
          }
        }}
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="xl">
            <VStack gap="3" p="3">
              <VStack gap="1" alignItems="flex-start" w="full">
                <LegacyDialog.Title>
                  {t('preferences.dialog.title')}
                </LegacyDialog.Title>
                <LegacyDialog.Description>
                  {t('preferences.dialog.description')}
                </LegacyDialog.Description>
              </VStack>

              <VStack gap="2" alignItems="flex-start" w="full">
                <Field.Root w="full">
                  <Field.Label fontSize="xs">
                    {t('preferences.dialog.fieldLabel')}
                  </Field.Label>
                  <Textarea
                    size="sm"
                    value={newPreferenceContent}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setNewPreferenceContent(e.target.value)
                    }
                    placeholder={t('preferences.dialog.placeholder')}
                    rows={4}
                    resize="vertical"
                  />
                  <Field.HelperText fontSize="xs">
                    {t('preferences.dialog.helperText')}
                  </Field.HelperText>
                </Field.Root>
              </VStack>

              <HStack gap="2" w="full" justifyContent="flex-end">
                <LegacyDialog.CloseTrigger asChild>
                  <Button size="sm" variant="outline">
                    {t('preferences.dialog.cancel')}
                  </Button>
                </LegacyDialog.CloseTrigger>
                <Button
                  size="sm"
                  colorPalette="terra"
                  onClick={handleCreatePreference}
                  disabled={!newPreferenceContent.trim()}
                >
                  {t('preferences.dialog.saveButton')}
                </Button>
              </HStack>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </VStack>
  );
}
